import { Buffer } from "buffer";
import * as anchor from "@coral-xyz/anchor";
import { Program, AnchorProvider } from "@coral-xyz/anchor";
import { Keypair, PublicKey, SendTransactionError } from "@solana/web3.js";
import bs58 from "bs58";
import { sha256 } from "js-sha256";
import { generatePDA } from "./pda";
import { uuidBs58 } from "./uuid";
import { fetchAllVotes, fetchVotesForSubmission } from "./vote";
import { Fartlab } from "idl/types/fartlab";
import { BN } from "bn.js";

// PDA seed for fart submissions
const SUBMISSION_PDA_SEED = "submission";

/**
 * Fetches all fart submissions from the blockchain
 */
export async function fetchAllFartSubmissions(program: Program<Fartlab>, provider: AnchorProvider) {
  const programId = program.programId;

  // ✅ Get the discriminator for "FartSubmission"
  const discriminator = bs58.encode(Buffer.from(sha256.digest("account:FartSubmission")).subarray(0, 8));

  const accounts = await provider.connection.getProgramAccounts(programId, {
    filters: [
      {
        memcmp: {
          offset: 0,
          bytes: discriminator,
        },
      },
    ],
  });

  const submissions = accounts.map((acc) =>
    program.coder.accounts.decode("fartSubmission", acc.account.data)
  );
  return submissions;
}


/**
 * Fetches all fart submissions linked to a championship.
 * Optionally filters by user ID.
 *
 * @param {Program<Fartlab>} program - The Anchor program instance.
 * @param {string} championshipUuid - The championship UUID.
 * @param {string} [userId] - (Optional) User's public key (Base58).
 * @returns {Promise<any[]>} - List of filtered submissions.
 */
export async function fetchFartSubmissionsForChampionship(
  program: Program<Fartlab>,
  provider: AnchorProvider,
  championshipUuid: string,
  userId?: string
) {
  const userIdText = userId ? ` and userId: ${userId}` : "";
  console.log(`🔍 Fetching Fart Submissions for Championship: ${championshipUuid} ${userIdText}`);
  
  // ✅ Fetch all submissions
  const allSubmissions = await fetchAllFartSubmissions(program, provider);
  // console.log(`🔍 Fetched ${allSubmissions.length} Fart Submissions`);

  // ✅ Use a single filter call for efficiency
  const filteredSubmissions = allSubmissions.filter(
    (submission) =>
      submission.championshipUuid === championshipUuid &&
      (!userId || new PublicKey(submission.user).toBase58() === userId) // Only filter by user if provided
  );

  console.log(`✅ Found ${filteredSubmissions.length} submissions for Championship: ${championshipUuid} ${userIdText}`);
  
  return filteredSubmissions;
}

/**
 * Fetches a single fart submission by UUID
 */
export async function getFartSubmission(
  program: Program<Fartlab>,
  uuid: string,
) {
  const fartSubmissionPDA = await generatePDA(program, SUBMISSION_PDA_SEED, uuid);
  console.log(`🔍 Fetching Fart Submission at PDA: ${new PublicKey(fartSubmissionPDA).toBase58()}`);

  const submission = await program.account.fartSubmission.fetch(fartSubmissionPDA);
  return submission;
}

/**
 * Fetches all submissions for a championship and maps votes efficiently.
 */
export async function fetchSubmissionsWithVotes(program: Program<Fartlab>, provider: AnchorProvider, championshipUuid: string,   userId?: string
) {
    console.log(`🔍 Fetching submissions and votes for Championship: ${championshipUuid}`);

    // ✅ Fetch all submissions
    const submissions = await fetchFartSubmissionsForChampionship(program, provider, championshipUuid, userId);

    // ✅ Fetch ALL votes instead of per submission
    const allVotes = await fetchAllVotes(program, provider);

    // ✅ Map votes to their respective submission UUIDs
    const votesBySubmission: Record<string, any[]> = {};
    allVotes.forEach(vote => {
        if (!votesBySubmission[vote.fartSubmissionUuid]) {
          votesBySubmission[vote.fartSubmissionUuid] = [];
        }
        votesBySubmission[vote.fartSubmissionUuid].push(vote);
    });

    // console.log(`✅ Found ${allVotes.length} votes for ${Object.keys(votesBySubmission).length} submissions`);
    // ✅ Attach votes to their submissions
    const submissionsWithVotes = submissions.map((submission) => {
      const submissionUuid = submission.uuid.toString(); // Ensure it's a string
      const votes = votesBySubmission[submissionUuid] || [];
      return {
        ...submission,
        votes,
        voteCount: votes.length,
      };
    });

    return submissionsWithVotes;
}

/**
 * Creates a new fart submission
 */
export async function createFartSubmission(program: Program<Fartlab>, payerKp: Keypair, owner: PublicKey, fartData: any, paymentTx: string) {
  const submittedAt = fartData.submittedAt || new BN(Math.floor(Date.now() / 1000)).add(new BN(100));
  const title = fartData.title || "Untitled Fart";
  const description = fartData.description || "No description provided";
  const uuid = fartData.uuid;
  const championshipUuid = fartData.championshipUuid;

  if (!uuid) {
    throw new Error("UUID is required to create a fart submission");
  }

  try {
    const tx = await program.methods
      .createFartSubmission(uuid, championshipUuid, title, description, submittedAt, paymentTx, owner)
      // .createFartSubmission(uuid, "c", "a", "b", submittedAt)
      .accounts({
        owner: payerKp.publicKey,
        // championship: championshipPDA,
      })
      .signers([payerKp])
      .rpc();

    console.log(`✅ Fart Submission created ${uuid}! Transaction:`, tx);
    return { success: true, tx };
  } catch (error) {
    // console.error("❌ Fart Submission creation failed:", error);
    return {
      success: false,
      error: {
        message: error?.error?.errorMessage || "Unknown error occurred.",
        code: error?.error?.errorCode?.number || null,
        logs: error?.logs || [],
        programErrorStack: error?._programErrorStack?.stack || [],
      },
    };
  }
}

/**
 * Updates a fart submission (Only submitter can update)
 */
export async function updateFartSubmission(
  program: Program<Fartlab>,
  wallet: Keypair,
  uuid: string,
  newTitle?: string,
  newDescription?: string,
  newPaymentTx?: string
) {
  try {
    const fartSubmissionPDA = await generatePDA(program, SUBMISSION_PDA_SEED, uuid);
    console.log(`🔍 Updating Fart Submission at PDA: ${new PublicKey(fartSubmissionPDA).toBase58()}`);

    const tx = await program.methods
      .updateFartSubmission(newTitle || null, newDescription || null, newPaymentTx || null)
      .accounts({
        fartSubmission: fartSubmissionPDA,
        owner: wallet.publicKey,
      })
      .signers([wallet])
      .rpc();

    console.log(`💨 Fart Submission updated! Transaction: ${tx}`);
    return tx;
  } catch (error) {
    console.error(`❌ Failed to update Fart Submission:`, error);
    throw error;
  }
}


/**
 * Deletes a fart submission
 */
export async function deleteFartSubmission(
  program: Program<Fartlab>,
  wallet: Keypair,
  uuid: string
) {
  try {
    const fartSubmissionPDA = await generatePDA(program, SUBMISSION_PDA_SEED, uuid);

    const tx = await program.methods
      .deleteFartSubmission()
      .accounts({
        fartSubmission: fartSubmissionPDA,
        owner: wallet.publicKey,
      })
      .signers([wallet])
      .rpc();

    // console.log(`🗑️ Fart Submission deleted! Transaction: ${tx}`);
    return tx;
  } catch (error) {
    console.error(`❌ Failed to delete Fart Submission:`, error);
    throw error;
  }
}


